import React, { Fragment, Component } from 'react';
import { HeroSection } from '../components/HeroSection';
import { AnimatedSection, ParallaxLayer } from '../components/AnimatedSection';
import { TextReveal, CountUp, Marquee, ShinyText, GradientText } from '../components/Animations';
import { CardGrid } from '../components/CardGrid';
import { SignalRibbon, IntegrationWall } from '../components/Graphics';
import { Shield, Zap, Target, Activity, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlaceholderGraphic } from '../components/PlaceholderGraphic';
// Decorative Concentric Circles Component
function ConcentricCircles({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`absolute pointer-events-none opacity-20 ${className}`} data-id="element-360">
      <div className="relative w-[800px] h-[800px] flex items-center justify-center" data-id="element-361">
        {[...Array(8)].map((_, i) => <div key={i} className={`absolute rounded-full border ${i === 3 ? 'border-picus-accent border-2 opacity-100' : 'border-gray-600'}`} style={{
        width: `${(i + 1) * 100}px`,
        height: `${(i + 1) * 100}px`
      }} data-id="element-362" />)}
      </div>
    </div>;
}
export function Home() {
  return <div className="pb-20 bg-picus-bg" data-id="element-363">
      <HeroSection eyebrow="Picus Platform" headline="Autonomous exposure validation for teams that need proof, not more noise" subhead="Picus Platform gives security teams real-time, context-rich validation across controls, exposures, and attack paths. It starts with the pain teams already feel today — proving controls work, knowing what to fix, and reducing manual effort — then extends that into autonomous validation with Picus Swarm." primaryCta={{
      label: 'See the Platform',
      to: '/platform'
    }} secondaryCta={{
      label: 'Get a Demo',
      to: '/contact'
    }} graphic={<SignalRibbon data-id="element-365" />} centered={false} data-id="element-364" />

      <div className="section-container space-y-0 mt-0" data-id="element-366">
        {/* Section 1 */}
        <section className="section-inner relative" data-id="element-367">
          <ParallaxLayer speed={0.3} scale={0.3} className="absolute -left-[400px] top-0 z-0 pointer-events-none" data-id="element-368">
            <ConcentricCircles data-id="element-369" />
          </ParallaxLayer>
          <AnimatedSection className="relative z-10" data-id="element-370">
            <ParallaxLayer speed={0.05} data-id="element-371">
              <h2 className="mb-6 max-w-3xl" data-id="element-372">
                99 problems, but finding an issue isn't one
              </h2>
            </ParallaxLayer>
            <p className="mb-12 max-w-2xl" data-id="element-373">
              Most teams already have more findings than they can work through.
              The real challenge is proving what works, knowing what matters,
              and acting before the backlog gets worse.
            </p>
          </AnimatedSection>
          <div className="relative z-10" data-id="element-374">
            <CardGrid columns={2} cards={[{
            title: 'Scanner Output',
            description: 'Thousands of alerts, disconnected dashboards, and theoretical risk.'
          }, {
            title: 'Validated Action',
            description: 'A short, prioritized list of exploitable paths with named owners.'
          }]} data-id="element-375" />
          </div>
          <AnimatedSection delay={0.2} className="mt-12 relative z-10" data-id="element-376">
            <PlaceholderGraphic description="Split-panel before/after. Left side: scanner output, alert queues, ticket pile, disconnected dashboards. Right side: short validated action list with named owners." data-id="element-377" />
          </AnimatedSection>
        </section>

        {/* Section 2 */}
        <section className="section-inner relative" data-id="element-378">
          <ParallaxLayer speed={0.3} scale={0.3} className="absolute -right-[400px] top-0 z-0 pointer-events-none" data-id="element-379">
            <ConcentricCircles data-id="element-380" />
          </ParallaxLayer>
          <AnimatedSection className="relative z-10" data-id="element-381">
            <ParallaxLayer speed={0.05} data-id="element-382">
              <h2 className="mb-6 max-w-3xl" data-id="element-383">Prove your controls work</h2>
            </ParallaxLayer>
            <p className="mb-8 max-w-2xl" data-id="element-384">
              Breach and Attack Simulation is still the fastest path to
              confidence in the stack you already own. Picus BAS helps teams
              validate whether prevention and detection controls are doing what
              they are supposed to do.
            </p>
            <Link to="/breach-and-attack-simulation" className="btn-secondary mb-12 flex items-center gap-2 w-fit" data-id="element-385">
              Explore BAS <ArrowRight className="w-3 h-3" data-id="element-386" />
            </Link>
          </AnimatedSection>
          <div className="relative z-10" data-id="element-387">
            <CardGrid columns={3} cards={[{
            title: 'Network Controls',
            description: 'Validate firewall and IPS effectiveness.'
          }, {
            title: 'Endpoint Controls',
            description: 'Test EDR prevention and telemetry.'
          }, {
            title: 'Detection Rules',
            description: 'Ensure SIEM alerts fire correctly.'
          }]} data-id="element-388" />
          </div>
        </section>

        {/* Section 3 */}
        <section className="section-inner relative" data-id="element-389">
          <ParallaxLayer speed={0.3} scale={0.3} className="absolute -left-[400px] top-0 z-0 pointer-events-none" data-id="element-390">
            <ConcentricCircles data-id="element-391" />
          </ParallaxLayer>
          <AnimatedSection className="relative z-10" data-id="element-392">
            <ParallaxLayer speed={0.05} data-id="element-393">
              <h2 className="mb-6 max-w-3xl" data-id="element-394">Know what actually matters</h2>
            </ParallaxLayer>
            <p className="mb-8 max-w-2xl" data-id="element-395">
              Discovery tells you what exists. Exposure Validation tells you
              what matters. Picus uses real payloads, real environment context,
              and actual control performance to separate theoretical risk from
              evidence-backed priority.
            </p>
            <Link to="/platform/exposure-validation" className="btn-secondary mb-12 flex items-center gap-2 w-fit" data-id="element-396">
              Explore Exposure Validation <ArrowRight className="w-3 h-3" data-id="element-397" />
            </Link>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10" data-id="element-398">
            <div className="p-10 border border-picus-border bg-picus-surface flex flex-row items-center gap-8" data-id="element-399">
              <ParallaxLayer speed={0.1} data-id="element-400">
                <div className="w-[20px] h-32 bg-picus-border" data-id="element-401" />
              </ParallaxLayer>
              <div data-id="element-402">
                <div className="font-mono text-[60px] leading-[72px] font-medium tracking-[2px] text-white mb-6" data-id="element-403">
                  10,000+
                </div>
                <div className="font-mono text-[12px] leading-[16px] font-medium uppercase tracking-[2.4px] text-white" data-id="element-404">
                  Discovered Exposures
                </div>
              </div>
            </div>
            <div className="p-10 border border-picus-border bg-picus-surface flex flex-row items-center gap-8" data-id="element-405">
              <ParallaxLayer speed={0.1} data-id="element-406">
                <div className="w-[20px] h-32 bg-picus-border" data-id="element-407" />
              </ParallaxLayer>
              <div data-id="element-408">
                <div className="font-mono text-[60px] leading-[72px] font-medium tracking-[2px] text-white mb-6" data-id="element-409">
                  450
                </div>
                <div className="font-mono text-[12px] leading-[16px] font-medium uppercase tracking-[2.4px] text-white" data-id="element-410">
                  Validated Exposures
                </div>
              </div>
            </div>
            <div className="p-10 border border-picus-border bg-picus-surface flex flex-row items-center gap-8" data-id="element-411">
              <ParallaxLayer speed={0.1} data-id="element-412">
                <div className="w-[20px] h-32 bg-picus-border" data-id="element-413" />
              </ParallaxLayer>
              <div data-id="element-414">
                <div className="font-mono text-[60px] leading-[72px] font-medium tracking-[2px] text-white mb-6" data-id="element-415">
                  12
                </div>
                <div className="font-mono text-[12px] leading-[16px] font-medium uppercase tracking-[2.4px] text-white" data-id="element-416">
                  Prioritized Actions
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section className="section-inner relative" data-id="element-417">
          <ParallaxLayer speed={0.3} scale={0.3} className="absolute -right-[400px] top-0 z-0 pointer-events-none" data-id="element-418">
            <ConcentricCircles data-id="element-419" />
          </ParallaxLayer>
          <AnimatedSection className="relative z-10" data-id="element-420">
            <ParallaxLayer speed={0.05} data-id="element-421">
              <h2 className="mb-6 max-w-3xl" data-id="element-422">Reduce the manual gap</h2>
            </ParallaxLayer>
            <p className="mb-8 max-w-2xl" data-id="element-423">
              Picus Swarm is the agentic operating layer of Picus Platform. It
              helps teams trigger validation on meaningful change, route the
              right context to the right owner, and reduce manual coordination
              between signal, validation, priority, and action.
            </p>
            <Link to="/platform/picus-swarm" className="btn-secondary mb-12 flex items-center gap-2 w-fit" data-id="element-424">
              Explore Picus Swarm <ArrowRight className="w-3 h-3" data-id="element-425" />
            </Link>
          </AnimatedSection>

          <div className="flex flex-wrap gap-0 items-center justify-center relative z-10" data-id="element-426">
            {['Signal', 'Validate', 'Prioritize', 'Act', 'Revalidate'].map((step, i) => <Fragment key={step} data-id="element-427">
                  <ParallaxLayer speed={0.02 * i} className="flex-1 min-w-[150px]" data-id="element-428">
                    <div className="h-full p-[40px] border border-picus-border bg-picus-surface flex items-center justify-center" data-id="element-429">
                      <span className="font-mono text-[14px] leading-[24px] font-medium uppercase tracking-[2px] text-white text-center" data-id="element-430">
                        {step}
                      </span>
                    </div>
                  </ParallaxLayer>
                  {i < 4 && <div className="px-4 text-picus-border hidden md:block" data-id="element-431">
                      <ArrowRight className="w-4 h-4" data-id="element-432" />
                    </div>}
                </Fragment>)}
          </div>

          <AnimatedSection delay={0.2} className="mt-12 relative z-10" data-id="element-433">
            <PlaceholderGraphic description="Workflow ribbon: signal → validate → prioritize → act → revalidate. Add small guardrail icons and human-in-loop toggles to signal governed autonomy." data-id="element-434" />
          </AnimatedSection>
        </section>

        {/* Section 5 */}
        <section className="section-inner relative" data-id="element-435">
          <ParallaxLayer speed={0.3} scale={0.3} className="absolute -left-[400px] top-0 z-0 pointer-events-none" data-id="element-436">
            <ConcentricCircles data-id="element-437" />
          </ParallaxLayer>
          <AnimatedSection className="relative z-10" data-id="element-438">
            <ParallaxLayer speed={0.05} data-id="element-439">
              <h2 className="mb-6 max-w-3xl" data-id="element-440">
                Built to respond to what changed, not what was scheduled
              </h2>
            </ParallaxLayer>
            <p className="mb-12 max-w-2xl" data-id="element-441">
              Static scans and calendar-based testing are too slow for
              environments and threats that change constantly. Picus Platform
              uses signals from your stack to trigger validation when it
              matters.
            </p>
          </AnimatedSection>
          <div className="relative z-10" data-id="element-442">
            <CardGrid columns={2} cards={[{
            title: 'Old Model',
            description: 'Calendar-driven validation. Testing happens quarterly regardless of environment changes.'
          }, {
            title: 'New Model',
            description: 'Signal-driven validation triggered by threat, asset, policy, and control changes.'
          }]} data-id="element-443" />
          </div>
          <AnimatedSection delay={0.2} className="mt-12 relative z-10" data-id="element-444">
            <PlaceholderGraphic description="Old model: calendar-driven validation. New model: signal-driven validation triggered by threat, asset, policy, and control changes." data-id="element-445" />
          </AnimatedSection>
        </section>

        {/* Section 6 */}
        <section className="section-inner relative text-center" data-id="element-446">
          <AnimatedSection className="mb-12 relative z-10" data-id="element-447">
            <ParallaxLayer speed={0.05} data-id="element-448">
              <h2 className="mb-6 max-w-3xl mx-auto" data-id="element-449">
                Your stack already has the signals
              </h2>
            </ParallaxLayer>
            <p className="max-w-2xl mx-auto mb-8" data-id="element-450">
              Picus gets context from the tools, controls, detections, policies,
              assets, and workflows already running in your environment. That
              context is what makes autonomous validation useful instead of
              generic.
            </p>
            <Link to="/integrations" className="btn-secondary flex items-center gap-2 w-fit mx-auto" data-id="element-451">
              See Integrations <ArrowRight className="w-3 h-3" data-id="element-452" />
            </Link>
          </AnimatedSection>
          <AnimatedSection className="relative z-10" data-id="element-453">
            <IntegrationWall data-id="element-454" />
          </AnimatedSection>
        </section>

        {/* Section 7 */}
        <section className="section-inner relative" data-id="element-455">
          <ParallaxLayer speed={0.3} scale={0.3} className="absolute -right-[400px] top-0 z-0 pointer-events-none" data-id="element-456">
            <ConcentricCircles data-id="element-457" />
          </ParallaxLayer>
          <AnimatedSection className="relative z-10" data-id="element-458">
            <ParallaxLayer speed={0.05} data-id="element-459">
              <h2 className="mb-6 max-w-3xl" data-id="element-460">
                See how the market responds to real threats
              </h2>
            </ParallaxLayer>
            <p className="mb-8 max-w-2xl" data-id="element-461">
              Picus Research turns validation data into practical intelligence
              for operators, leaders, and buyers. Explore the Threat Response
              Index, Red Report, Blue Report, and Picus Labs.
            </p>
            <Link to="/research" className="btn-secondary mb-12 flex items-center gap-2 w-fit" data-id="element-462">
              Explore Research <ArrowRight className="w-3 h-3" data-id="element-463" />
            </Link>
          </AnimatedSection>
          <div className="relative z-10" data-id="element-464">
            <CardGrid columns={4} cards={[{
            title: 'Threat Response Index',
            to: '/research/threat-response-index',
            tag: 'Featured'
          }, {
            title: 'Red Report',
            to: '/research/red-report'
          }, {
            title: 'Blue Report',
            to: '/research/blue-report'
          }, {
            title: 'Picus Labs',
            to: '/research/picus-labs'
          }]} data-id="element-465" />
          </div>
        </section>

        {/* Section 8 */}
        <section className="section-inner relative text-center" data-id="element-466">
          <AnimatedSection className="mb-12 relative z-10" data-id="element-467">
            <ParallaxLayer speed={0.05} data-id="element-468">
              <h2 className="mb-6 max-w-3xl mx-auto" data-id="element-469">
                Built for teams that need evidence, not guesses
              </h2>
            </ParallaxLayer>
            <p className="max-w-2xl mx-auto mb-12" data-id="element-470">
              Picus helps security teams optimize defense, improve exposure
              awareness, and scale offensive testing. It is trusted by teams
              that need proof they can defend to peers, leadership, and boards.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative z-10" data-id="element-471">
              {[{
              top: '4.9 on G2',
              bottom: ''
            }, {
              top: '4.8 on Gartner',
              bottom: 'Peer Insights'
            }, {
              top: '#1 Innovation Index',
              bottom: 'on Frost Radar 2026'
            }, {
              top: '95% Recommendation',
              bottom: 'Rate'
            }].map((stat, i) => <div key={i} className="p-8 border border-picus-border bg-picus-surface flex flex-col items-center justify-center gap-4" data-id="element-472">
                  <div className="flex gap-1 text-picus-accentSecondary" data-id="element-473">
                    {[...Array(5)].map((_, j) => <svg key={j} className="w-4 h-4 fill-current" viewBox="0 0 24 24" data-id="element-474">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-475" />
                      </svg>)}
                  </div>
                  <div className="font-mono text-[12px] leading-[16px] font-medium uppercase tracking-[2.4px] text-white text-center" data-id="element-476">
                    {stat.top}
                    {stat.bottom && <br data-id="element-477" />}
                    {stat.bottom}
                  </div>
                </div>)}
            </div>
          </AnimatedSection>

          <ParallaxLayer speed={0.1} className="w-full overflow-hidden opacity-50 grayscale mt-20 relative z-10" data-id="element-478">
            <Marquee data-id="element-479">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="text-xl font-bold tracking-widest whitespace-nowrap px-8" data-id="element-480">
                  ENTERPRISE {i}
                </div>)}
            </Marquee>
          </ParallaxLayer>
        </section>

        {/* Section 9 */}
        <section className="section-inner relative text-center py-48" data-id="element-481">
          <ParallaxLayer speed={0.4} scale={0.5} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0 pointer-events-none" data-id="element-482">
            <ConcentricCircles data-id="element-483" />
          </ParallaxLayer>
          <AnimatedSection className="relative z-10" data-id="element-484">
            <ParallaxLayer speed={0.05} data-id="element-485">
              <h2 className="text-[50px] leading-[56px] tracking-[8.5px] mb-8 max-w-4xl mx-auto" data-id="element-486">
                Start with the problem you need to solve today
              </h2>
            </ParallaxLayer>
            <p className="mb-12 max-w-2xl mx-auto" data-id="element-487">
              Need BAS? Need to know what to fix? Need to reduce the manual gap
              between findings and action? Picus Platform gives you immediate
              value now and a more scalable operating model over time.
            </p>
            <Link to="/contact" className="btn-primary" data-id="element-488">
              Get a Demo
            </Link>
          </AnimatedSection>
        </section>
      </div>
    </div>;
}